import { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_5ecc19a3-715e-40b2-bee7-8ae4aeb4c8cb/artifacts/ladwjbmi_4926BBCA-F27B-484F-9467-294F35FF2347.PNG';

function RevealText({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <div ref={ref} className="overflow-hidden">
      <motion.div
        className={className}
        initial={{ y: '100%', opacity: 0 }}
        animate={isInView ? { y: '0%', opacity: 1 } : {}}
        transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
}

/* ─── ABOUT HERO ─── */
function AboutHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '15%']);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center bg-xmx-black px-6 sm:px-12 overflow-hidden">
      {/* Large faded background letter */}
      <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 pointer-events-none select-none">
        <span className="font-cabinet text-[40vw] font-black uppercase text-white/[0.015] leading-none">
          A
        </span>
      </div>

      <motion.div className="max-w-5xl mx-auto relative z-10 pt-32" style={{ y: textY }}>
        <motion.span
          className="font-mono text-[10px] tracking-[0.3em] uppercase text-xmx-accent block mb-8"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          About XMX Ventures
        </motion.span>

        <motion.h1
          className="font-cabinet text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight font-bold text-xmx-white mb-8"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          We are the infrastructure behind
          <br />
          <span className="text-xmx-grey">the brands you'll know tomorrow.</span>
        </motion.h1>

        <motion.div
          className="w-16 h-[1px] bg-xmx-accent/50"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
          style={{ transformOrigin: 'left' }}
        />
      </motion.div>
    </section>
  );
}

/* ─── PHILOSOPHY SECTION ─── */
function PhilosophySection() {
  return (
    <section className="relative bg-xmx-black py-28 sm:py-40 px-6 sm:px-12" data-testid="philosophy-section">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-8">
          {/* Left: Label */}
          <div className="lg:col-span-4">
            <RevealText>
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted">
                Our Philosophy
              </span>
            </RevealText>
          </div>

          {/* Right: Content */}
          <div className="lg:col-span-8">
            <RevealText delay={0.1}>
              <p className="font-cabinet text-2xl sm:text-3xl lg:text-4xl leading-[1.3] tracking-tight font-medium text-xmx-white mb-10">
                We don't advise. We operate. Every brand we build is a system
                we run — from product inception to the consumer's hands.
              </p>
            </RevealText>
            <RevealText delay={0.25}>
              <p className="font-mono text-sm leading-relaxed text-xmx-grey max-w-xl">
                XMX Ventures was founded on a singular conviction: that the
                gap between product potential and market success is not
                creative — it's operational. We close that gap through
                controlled, end-to-end execution.
              </p>
            </RevealText>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── PRINCIPLES SECTION ─── */
function PrinciplesSection() {
  const principles = [
    {
      number: '01',
      title: 'Vertical Integration',
      text: 'We own every layer of the process. Design, development, manufacturing, fulfillment, and channel management exist under a single operational roof.',
    },
    {
      number: '02',
      title: 'Speed as Strategy',
      text: 'Markets move. We move faster. Our systems are designed for rapid iteration — from concept validation to market deployment in compressed timelines.',
    },
    {
      number: '03',
      title: 'Data-Driven Conviction',
      text: 'Every decision is informed by real market signals. We combine quantitative analysis with deep consumer intuition to identify and capture demand.',
    },
    {
      number: '04',
      title: 'Relentless Standards',
      text: "We don't ship products — we ship quality. Every brand we create meets the standard we'd expect as consumers, not just as operators.",
    },
  ];

  return (
    <section className="relative bg-xmx-dark py-28 sm:py-40 px-6 sm:px-12" data-testid="principles-section">
      <div className="max-w-6xl mx-auto">
        <RevealText>
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted block mb-16 sm:mb-24">
            Core Principles
          </span>
        </RevealText>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
          {principles.map((p, i) => (
            <PrincipleCard key={p.number} principle={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PrincipleCard({ principle, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      className="group border-t border-l border-white/[0.06] p-8 sm:p-12"
      data-testid={`principle-${index}`}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
    >
      <span className="font-mono text-[10px] tracking-[0.2em] text-xmx-accent block mb-6">
        {principle.number}
      </span>
      <h3 className="font-cabinet text-xl sm:text-2xl tracking-tight font-bold text-xmx-white uppercase mb-4">
        {principle.title}
      </h3>
      <p className="font-mono text-xs sm:text-sm leading-relaxed text-xmx-muted group-hover:text-xmx-grey transition-colors duration-500">
        {principle.text}
      </p>
    </motion.div>
  );
}

/* ─── METRICS SECTION ─── */
function MetricsSection() {
  const metrics = [
    { value: 'U.S.', label: 'Market Focus' },
    { value: 'End-to-End', label: 'Execution Model' },
    { value: 'Multi-Brand', label: 'Portfolio Strategy' },
    { value: 'Full-Stack', label: 'Operational Depth' },
  ];

  return (
    <section className="relative bg-xmx-black py-28 sm:py-40 px-6 sm:px-12" data-testid="metrics-section">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-4">
          {metrics.map((m, i) => (
            <MetricItem key={m.label} metric={m} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function MetricItem({ metric, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div
      ref={ref}
      className="text-center sm:text-left"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
    >
      <span className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-xmx-white block mb-2">
        {metric.value}
      </span>
      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-xmx-muted">
        {metric.label}
      </span>
    </motion.div>
  );
}

/* ─── CLOSING STATEMENT ─── */
function ClosingSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'center center'],
  });
  const opacity = useTransform(scrollYProgress, [0, 0.6], [0, 1]);
  const y = useTransform(scrollYProgress, [0, 0.6], [50, 0]);

  return (
    <section ref={ref} className="relative min-h-[60vh] flex items-center bg-xmx-black px-6 sm:px-12 border-t border-white/[0.04]">
      <motion.div className="max-w-4xl mx-auto text-center" style={{ opacity, y }}>
        <h2 className="font-cabinet text-3xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-bold text-xmx-white mb-6">
          Built to operate.
          <br />
          <span className="text-xmx-grey">Designed to last.</span>
        </h2>
        <p className="font-mono text-xs tracking-wide text-xmx-muted max-w-md mx-auto">
          Every system, every brand, every decision reflects a commitment to enduring quality and operational excellence.
        </p>
      </motion.div>
    </section>
  );
}

/* ─── ABOUT FOOTER ─── */
function AboutFooter() {
  return (
    <footer className="bg-xmx-black border-t border-white/[0.06] px-6 sm:px-12 py-10" data-testid="about-footer">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <img src={LOGO_URL} alt="XMX Ventures" className="h-8 w-auto opacity-40" />
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-xmx-muted">
          {new Date().getFullYear()} XMX Ventures
        </span>
      </div>
    </footer>
  );
}

export default function About() {
  return (
    <div data-testid="about-page" className="bg-xmx-black">
      <AboutHero />
      <PhilosophySection />
      <PrinciplesSection />
      <MetricsSection />
      <ClosingSection />
      <AboutFooter />
    </div>
  );
}
